import React, { useState, useEffect } from 'react';
import { 
  DollarSign, TrendingUp, Users, Trophy, Eye, Heart, 
  BookOpen, Lock, ChevronRight, ArrowUpRight, ArrowDownRight, Settings
} from 'lucide-react';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';
import { getCreatorEarnings } from '../services/api';
import { CreatorPoolSettings } from './CreatorPoolSettings';
import { toast } from 'sonner';

const COLORS = ['#E63946', '#FF6B35', '#FFB703', '#2EC4B6', '#8338EC'];

export function CreatorEarningsDashboard() {
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('6m');
  const [showPoolSettings, setShowPoolSettings] = useState(false);

  useEffect(() => {
    fetchEarnings();
  }, []);

  const fetchEarnings = async () => {
    try {
      const res = await getCreatorEarnings();
      setEarnings(res.data);
    } catch (error) {
      console.error('Error fetching earnings:', error);
      toast.error('Failed to load earnings data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-[var(--surface)] rounded-xl p-6 animate-pulse">
              <div className="h-4 w-20 bg-[var(--border)] rounded mb-4" />
              <div className="h-8 w-24 bg-[var(--border)] rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!earnings) {
    return (
      <div className="text-center py-12">
        <Lock className="mx-auto mb-4 text-[var(--text-secondary)]" size={48} />
        <p className="text-[var(--text-secondary)]">Unable to load earnings data</p>
      </div>
    );
  }

  const totalEarnings = earnings.total_earnings || 0;
  const totalDonations = earnings.total_donations || 0;
  const totalPrizes = earnings.total_prizes || 0;
  const followers = earnings.followers_count || 0;

  // Calculate growth (mock for demo - would compare to previous period)
  const donationGrowth = 12.5;
  const followerGrowth = 8.3;

  // Show Pool Settings view
  if (showPoolSettings) {
    return (
      <div className="space-y-6">
        <button 
          onClick={() => setShowPoolSettings(false)}
          className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--foreground)]"
        >
          <ChevronRight size={16} className="rotate-180" />
          Back to Earnings
        </button>
        <CreatorPoolSettings />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="creator-earnings-dashboard">
      {/* Header with Pool Settings Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)] bg-[var(--surface)] border border-[var(--border)] rounded-lg px-4 py-3">
          <Lock size={16} />
          <span>This earnings data is private and only visible to you</span>
        </div>
        <button
          onClick={() => setShowPoolSettings(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[var(--surface)] border border-[var(--border)] rounded-lg hover:border-[var(--primary)] transition-colors"
        >
          <Settings size={16} />
          <span className="text-sm">Pool Settings</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Earnings"
          value={`$${totalEarnings.toFixed(2)}`}
          icon={DollarSign}
          color="text-green-500"
          bgColor="bg-green-500/10"
        />
        <StatCard
          title="Donations Received"
          value={`$${totalDonations.toFixed(2)}`}
          icon={Heart}
          color="text-[var(--primary)]"
          bgColor="bg-[var(--primary)]/10"
          trend={donationGrowth}
        />
        <StatCard
          title="Prize Winnings"
          value={`$${totalPrizes.toFixed(2)}`}
          icon={Trophy}
          color="text-yellow-500"
          bgColor="bg-yellow-500/10"
        />
        <StatCard
          title="Supporters"
          value={followers}
          icon={Users}
          color="text-blue-500"
          bgColor="bg-blue-500/10"
          trend={followerGrowth}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Earnings Chart */}
        <div className="lg:col-span-2 bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-teko">Earnings Over Time</h3>
              <p className="text-sm text-[var(--text-secondary)]">Monthly breakdown of your income</p>
            </div>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="px-3 py-1 bg-[var(--background)] border border-[var(--border)] rounded-lg text-sm"
            >
              <option value="6m">Last 6 months</option>
              <option value="3m">Last 3 months</option>
              <option value="1y">Last year</option>
            </select>
          </div>
          
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={earnings.monthly_earnings || []}>
                <defs>
                  <linearGradient id="colorDonations" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#E63946" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#E63946" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorPrizes" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#FFB703" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#FFB703" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis 
                  dataKey="month" 
                  stroke="var(--text-secondary)"
                  tick={{ fill: 'var(--text-secondary)', fontSize: 12 }}
                />
                <YAxis 
                  stroke="var(--text-secondary)"
                  tick={{ fill: 'var(--text-secondary)', fontSize: 12 }}
                  tickFormatter={(value) => `$${value}`}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'var(--surface)', 
                    border: '1px solid var(--border)',
                    borderRadius: '8px'
                  }}
                  formatter={(value) => [`$${value.toFixed(2)}`, '']}
                />
                <Area 
                  type="monotone" 
                  dataKey="donations" 
                  stroke="#E63946" 
                  fillOpacity={1} 
                  fill="url(#colorDonations)"
                  name="Donations"
                />
                <Area 
                  type="monotone" 
                  dataKey="prizes" 
                  stroke="#FFB703" 
                  fillOpacity={1} 
                  fill="url(#colorPrizes)"
                  name="Prizes"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Earnings Breakdown */}
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6">
          <h3 className="text-xl font-teko mb-6">Earnings Breakdown</h3>
          
          <div className="h-48 mb-4">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={[
                    { name: 'Donations', value: totalDonations },
                    { name: 'Prizes', value: totalPrizes },
                  ]}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={70}
                  paddingAngle={5}
                  dataKey="value"
                >
                  <Cell fill="#E63946" />
                  <Cell fill="#FFB703" />
                </Pie>
                <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#E63946]" />
                <span className="text-sm">Donations</span>
              </div>
              <span className="font-medium">${totalDonations.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#FFB703]" />
                <span className="text-sm">Prizes</span>
              </div>
              <span className="font-medium">${totalPrizes.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Performing Comics */}
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-teko">Top Performing Comics</h3>
            <BookOpen className="text-[var(--text-secondary)]" size={20} />
          </div>
          
          {earnings.top_comics && earnings.top_comics.length > 0 ? (
            <div className="space-y-3">
              {earnings.top_comics.map((comic, index) => (
                <div 
                  key={comic.id}
                  className="flex items-center justify-between p-3 bg-[var(--background)] rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 flex items-center justify-center bg-[var(--primary)]/20 text-[var(--primary)] rounded-full text-sm font-bold">
                      {index + 1}
                    </span>
                    <div>
                      <p className="font-medium text-sm line-clamp-1">{comic.title}</p>
                      <p className="text-xs text-[var(--text-secondary)]">{comic.episode_count} episodes</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-2 text-sm">
                      <Eye size={14} className="text-[var(--text-secondary)]" />
                      {comic.views?.toLocaleString()}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-[var(--text-secondary)]">
                      <Heart size={12} />
                      {comic.likes?.toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[var(--text-secondary)] text-center py-8">
              No comics yet. Start creating!
            </p>
          )}
        </div>

        {/* Recent Transactions */}
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-teko">Recent Activity</h3>
            <TrendingUp className="text-[var(--text-secondary)]" size={20} />
          </div>
          
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {earnings.recent_donations && earnings.recent_donations.length > 0 ? (
              earnings.recent_donations.map((donation, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between p-3 bg-[var(--background)] rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[var(--primary)]/20 flex items-center justify-center">
                      <Heart size={16} className="text-[var(--primary)]" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">Donation received</p>
                      <p className="text-xs text-[var(--text-secondary)]">
                        {new Date(donation.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <span className="text-green-500 font-medium">
                    +${donation.amount?.toFixed(2)}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-[var(--text-secondary)] text-center py-8">
                No recent activity
              </p>
            )}
            
            {earnings.prize_history && earnings.prize_history.map((prize, index) => (
              <div 
                key={`prize-${index}`}
                className="flex items-center justify-between p-3 bg-[var(--background)] rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-yellow-500/20 flex items-center justify-center">
                    <Trophy size={16} className="text-yellow-500" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Prize won - {prize.category}</p>
                    <p className="text-xs text-[var(--text-secondary)]">
                      {new Date(prize.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <span className="text-yellow-500 font-medium">
                  +${prize.amount?.toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, icon: Icon, color, bgColor, trend }) {
  return (
    <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-5 hover:border-[var(--primary)]/30 transition-colors">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-[var(--text-secondary)] mb-1">{title}</p>
          <p className="text-2xl font-teko font-semibold">{value}</p>
          {trend !== undefined && (
            <div className={`flex items-center gap-1 mt-1 text-xs ${trend >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {trend >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
              {Math.abs(trend)}% from last month
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl ${bgColor}`}>
          <Icon className={color} size={20} />
        </div>
      </div>
    </div>
  );
}

export default CreatorEarningsDashboard;
